import { useFeatureFlag, FeatureFlagKey } from './useFeatureFlags';
import { initOgAnalytics, isOgAnalyticsEnabled } from '../services/analytics/ogAnalytics';

/**
 * Gates @opengov/analytics behind the `psp-og-analytics` LaunchDarkly flag and
 * creates the shared client the first time the flag is seen on.
 *
 * **Deliberate exception to render purity.** Creation happens during render,
 * not in an effect: `useShellAnalytics` reads the shared client synchronously
 * in the same render, so the first pageview and identify fire on the render
 * where the flag turns on, not one render later. `useMountEffect` does not fit,
 * because the flag resolves after mount, and CLAUDE.md names no sanctioned
 * wrapper for a flag transition.
 *
 * It is safe because `initOgAnalytics()` is idempotent and one-way. A render
 * React discards (StrictMode, a concurrent transition) can only create the
 * client for a flag that is on, which the render that commits normally sees
 * too. If the flag is switched off in between, the result is the same as any
 * mid-session switch-off: it takes effect on the next reload (below). There is
 * nothing to undo, since the client is never torn down.
 *
 * The pure alternative is a component rendered only while the flag is on,
 * which creates the client in `useMountEffect`, sets state, and renders the
 * analytics hooks on the next pass. That is more code for the same behaviour,
 * plus the one-render delay.
 *
 * Once created, the client lives for the rest of the page load, so turning the
 * flag off takes effect on the next reload. That keeps a session all-or-nothing:
 * autocapture (off today) binds `document` at creation and cannot be cleanly
 * unbound, so a mid-session off would stop pageviews while it kept going.
 */
export function useOgAnalyticsEnabled(): boolean {
  const flagEnabled = useFeatureFlag(FeatureFlagKey.PSP_OG_ANALYTICS);
  if (flagEnabled) initOgAnalytics();
  return isOgAnalyticsEnabled();
}
